import React from 'react';
import '../assets/styles/Blog.css';

const blogPosts = [
    {
        id: 'allowance-by-age-guide',
        title: 'The Ultimate Allowance Guide By Age: How Much Should You Pay?',
        date: 'March 2026',
        readTime: '6 min read',
        excerpt: 'Wondering how much allowance is appropriate for your 7-year-old versus your 14-year-old? We break down the research-backed averages and best practices by age group.',
        image: '📈',
        content: `
# The Ultimate Allowance Guide By Age: How Much Should You Pay?

One of the most common questions we hear from parents is: *"How much allowance should I be paying my kids?"*

The answer depends heavily on their age, but there are some golden rules you can follow.

## The "Rule of Thumb": $1 Per Year of Age

Many financial experts recommend the "$1 per week per year of age" rule. 
- A 6-year-old gets $6/week
- A 10-year-old gets $10/week
- A 15-year-old gets $15/week

However, this isn't a strict law. It depends on what you expect them to pay for with that money.

## Guidelines by Age Group

### Ages 5-7: The "Foundation" Years
**Recommended Amount:** $3 - $5 per week
**What it teaches:** Basic coin recognition, the concept of a transaction, and patience.
**What they buy:** Small treats, cheap toys, trading cards.

### Ages 8-11: The "Goal Setting" Years
**Recommended Amount:** $8 - $12 per week
**What it teaches:** Saving for a medium-term goal, understanding the cost of items they want.
**What they buy:** Video games, movie tickets with friends, branded clothing.

### Ages 12-15: The "Responsibility" Years
**Recommended Amount:** $15 - $25 per week
**What it teaches:** Budgeting for required expenses vs. fun money.
**What they buy:** Cell phone bill contribution, meals out with friends, saving for a car.

## The DadBucks Method

Instead of a flat weekly handout, we recommend treating allowance like a salary tied to responsibilities. With DadBucks, you can set a baseline and let them earn bonuses through quests and streaks!
        `.trim()
    },
    {
        id: 'financial-literacy-for-kids',
        title: 'Financial Literacy for Kids: A Comprehensive Parent’s Guide',
        date: 'March 2026',
        readTime: '8 min read',
        excerpt: 'Schools rarely teach personal finance. Discover the core concepts you need to teach your children before they leave the nest, and the best activities to make those lessons stick.',
        image: '🏫',
        content: `
# Financial Literacy for Kids: A Comprehensive Parent’s Guide

Personal finance is rarely taught in schools, which means the responsibility falls squarely on parents. But where do you start?

Here are the core concepts your children need to master, broken down into actionable steps.

## Core Concept 1: Earning vs. Handouts
Money comes from creating value, not from an ATM. 
**Activity:** Connect allowance to chores. Pay for extra "above and beyond" tasks like washing the car to teach the entrepreneurial spirit.

## Core Concept 2: The Three Jars System
Every dollar earned should have a purpose. Teach the classic "Spend, Save, Give" method.
**Activity:** When they earn their allowance using DadBucks, require them to put 10% into savings and 10% into a giving fund before they can touch the "spend" money.

## Core Concept 3: Delayed Gratification
The inability to wait is the root of most bad financial decisions (like high-interest credit card debt).
**Activity:** Set up a DadBucks Goal for an expensive item. Have them watch the progress bar inch closer week by week. Do not front them the cash.

## Core Concept 4: Beware of Subscriptions & Digital Spending
Today's kids are bombarded with microtransactions and recurring subscriptions (Roblox, FortNite).
**Activity:** Give them a set digital budget. If they want a $10/month game subscription, they have to ensure they earn enough DadBucks each month to cover it.

Start early. Financial literacy is a marathon, not a sprint.
        `.trim()
    },
    {
        id: 'teach-kids-money',
        title: '5 Ways to Teach Kids About Money (That Actually Work)',
        date: 'January 2026',
        readTime: '5 min read',
        excerpt: 'Financial literacy starts at home. Here are proven strategies to help your children develop healthy money habits that will last a lifetime.',
        image: '💰',
        content: `
# 5 Ways to Teach Kids About Money (That Actually Work)

Teaching children about money doesn't have to be complicated. Here are five proven strategies that make learning about finances fun and effective.

## 1. Start With Earning, Not Giving

The biggest mistake parents make is simply giving kids money. Instead, connect money to work:

- **Create a chore system** where kids earn for completed tasks
- **Be consistent** with payouts - payday should feel real
- **Celebrate their earnings** to reinforce the work-reward connection

## 2. Make Savings Visible

Kids are visual learners. Abstract concepts like "saving" need to be tangible:

- Use clear jars so they can see money accumulate
- Create a savings goal chart on the wall
- **Use apps like DadBucks** to track progress digitally

## 3. Let Them Make (Small) Mistakes

This is the hardest one for parents. But letting kids make poor purchasing decisions with small amounts teaches them more than any lecture.

> "My son spent his entire savings on a cheap toy that broke in two days. He was devastated—but he never made that mistake again."

## 4. Play Money Games

Turn learning into play:

- **Monopoly** teaches investing and property
- **The Allowance Game** focuses on saving
- **Real-life shopping math** at the grocery store

## 5. Model Good Behavior

Kids learn by watching. Let them see you:

- Budgeting and planning purchases
- Saving for goals
- Making trade-offs ("We're not eating out tonight so we can save for vacation")

---

**Ready to put these strategies into practice?** DadBucks makes it easy to implement a chore-based earning system that teaches kids the value of money.
    `.trim()
    },
    {
        id: 'chore-charts-fail',
        title: 'Why Chore Charts Fail (And What Actually Works)',
        date: 'January 2026',
        readTime: '4 min read',
        excerpt: 'Tired of sticker charts that lose their magic after a week? Discover why traditional chore systems fall apart and what to do instead.',
        image: '📋',
        content: `
# Why Chore Charts Fail (And What Actually Works)

You've been there. Week one: excitement, stickers everywhere. Week three: the chart is ignored, half-empty, collecting dust on the fridge.

Here's why—and what actually works.

## The Problem With Traditional Chore Charts

### 1. External Motivation Fades
Stickers are exciting... for about a week. Once the novelty wears off, so does the motivation.

### 2. No Real Consequences
Missing a sticker doesn't actually *mean* anything to kids. There's no tangible impact.

### 3. Parent-Dependent
If you forget to put up a sticker, the whole system breaks down. One missed day becomes three, then it's abandoned.

### 4. No Connection to Real Life
Stickers don't teach kids how the real world works. Adults don't get stickers for going to work.

## What Actually Works

### Connect Chores to Earning

Instead of abstract "points," tie chores to something kids actually want: money they can spend.

- **Vacuum the living room**: $2.00
- **Take out trash**: $1.00
- **Load dishwasher**: $1.50

This mirrors the real world. Work = pay. No work = no pay.

### Make the Rewards Visible

Use a digital system (like DadBucks) where kids can *see* their balance grow in real-time. Watching that number increase is inherently motivating.

### Add Goals

Help kids set savings goals for things they actually want:
- New video game: $60
- Movie night with friends: $30
- Special toy: $25

When kids connect daily chores to future purchases, motivation becomes self-sustaining.

### Create Streaks

Gamification works. When kids see "7-day streak 🔥" they don't want to break it. Build in streak bonuses for sustained effort.

---

**Bottom line**: Chore charts fail because they lack real-world connection. Systems that connect work to earnings, make progress visible, and include goals actually stick.
    `.trim()
    },
    {
        id: 'age-appropriate-chores',
        title: 'Age-Appropriate Chores for Kids (Complete Guide)',
        date: 'January 2026',
        readTime: '6 min read',
        excerpt: 'Not sure what chores your child can handle? This complete guide breaks down appropriate tasks by age, from toddlers to teenagers.',
        image: '🧹',
        content: `
# Age-Appropriate Chores for Kids (Complete Guide)

One of the most common questions parents ask: "What chores can my kid actually do?" This guide breaks it down by age group.

## Ages 2-3: The Helpers

At this age, kids want to help. Embrace it! Simple tasks include:

- Put toys in a bin
- Put dirty clothes in hamper
- Help wipe up spills
- Carry unbreakable items to the table

**Tip**: Don't expect perfection. The goal is building the habit of helping.

## Ages 4-5: Building Independence

Preschoolers can handle more:

- Make their bed (it won't be perfect—that's OK)
- Set napkins and utensils on the table
- Dust low furniture
- Sort laundry by color
- Water plants
- Feed pets with supervision

## Ages 6-8: Real Responsibility

Elementary school kids are ready for legitimate chores:

- Take out the trash
- Fold laundry
- Sweep floors
- Help load/unload dishwasher
- Clean their room independently
- Make simple snacks
- Sort recycling

**Suggested pay**: $0.50 - $2.00 per task

## Ages 9-11: Developing Skills

Pre-teens can handle complex multi-step tasks:

- Load and run dishwasher
- Vacuum and mop floors
- Clean bathrooms
- Help with meal prep
- Take care of pets independently
- Yard work (raking, weeding)
- Bring in mail and packages

**Suggested pay**: $2.00 - $5.00 per task

## Ages 12-14: Almost Adult

Teenagers should be contributing significantly:

- Cook simple meals
- Do their own laundry start to finish
- Clean whole rooms thoroughly
- Babysit younger siblings
- Mow the lawn
- Wash the car
- Help with grocery shopping

**Suggested pay**: $5.00 - $15.00 per task

## Ages 15+: Real-World Prep

High schoolers should be nearly self-sufficient:

- All household cleaning
- Meal planning and cooking
- Lawn care and outdoor maintenance
- Simple home repairs
- Running errands
- Managing their own schedule

**Suggested pay**: $10.00 - $25.00 per task, or move to a weekly allowance

---

## Common Mistakes to Avoid

1. **Doing it for them** because it's faster
2. **Not being consistent** with expectations
3. **Nagging** instead of having consequences
4. **Not paying** because "they should contribute anyway"

The goal is to raise capable adults. Every chore teaches life skills.

---

**Pro tip**: Use DadBucks to track chores and pay automatically. Kids see their balance grow, and you never have to nag about "did you do your chores?"
    `.trim()
    }
];

const BlogList = ({ onSelectPost }) => {
    return (
        <div className="blog-list">
            {blogPosts.map(post => (
                <article key={post.id} className="blog-card" onClick={() => onSelectPost(post)}>
                    <div className="blog-card-image">{post.image}</div>
                    <div className="blog-card-content">
                        <h2>{post.title}</h2>
                        <div className="blog-meta">
                            <span>{post.date}</span>
                            <span>•</span>
                            <span>{post.readTime}</span>
                        </div>
                        <p>{post.excerpt}</p>
                        <span className="read-more">Read more →</span>
                    </div>
                </article>
            ))}
        </div>
    );
};

const BlogPost = ({ post, onBack, onSignup }) => {
    // Simple markdown-like rendering
    const renderContent = (content) => {
        return content.split('\n').map((line, i) => {
            if (line.startsWith('# ')) {
                return <h1 key={i}>{line.substring(2)}</h1>;
            }
            if (line.startsWith('## ')) {
                return <h2 key={i}>{line.substring(3)}</h2>;
            }
            if (line.startsWith('### ')) {
                return <h3 key={i}>{line.substring(4)}</h3>;
            }
            if (line.startsWith('> ')) {
                return <blockquote key={i}>{line.substring(2)}</blockquote>;
            }
            if (line.startsWith('- **')) {
                const match = line.match(/- \*\*(.+?)\*\*:?\s*(.*)/);
                if (match) {
                    return <li key={i}><strong>{match[1]}</strong>{match[2] ? `: ${match[2]}` : ''}</li>;
                }
            }
            if (line.startsWith('- ')) {
                return <li key={i}>{line.substring(2)}</li>;
            }
            if (line.startsWith('---')) {
                return <hr key={i} />;
            }
            if (line.trim() === '') {
                return <br key={i} />;
            }
            // Handle bold text
            const boldPattern = /\*\*(.+?)\*\*/g;
            const parts = line.split(boldPattern);
            if (parts.length > 1) {
                return (
                    <p key={i}>
                        {parts.map((part, j) =>
                            j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                        )}
                    </p>
                );
            }
            return <p key={i}>{line}</p>;
        });
    };

    return (
        <article className="blog-post">
            <button className="blog-back-btn" onClick={onBack}>← Back to Blog</button>
            <div className="blog-post-header">
                <span className="blog-post-image">{post.image}</span>
                <div className="blog-post-meta">
                    <span>{post.date}</span>
                    <span>•</span>
                    <span>{post.readTime}</span>
                </div>
            </div>
            <div className="blog-post-content">
                {renderContent(post.content)}
            </div>
            <div className="blog-cta">
                <h3>Ready to try DadBucks?</h3>
                <p>Set up your family's chore system in minutes.</p>
                <button onClick={onSignup} className="cta-button">Get Started Free</button>
            </div>
        </article>
    );
};

const Blog = ({ onBack, onSignup }) => {
    const [selectedPost, setSelectedPost] = React.useState(null);

    return (
        <div className="blog-container">
            <header className="blog-header">
                <div className="blog-header-content">
                    <button className="blog-home-btn" onClick={onBack}>← Home</button>
                    <h1>DadBucks Blog</h1>
                    <p>Tips, guides, and insights for teaching kids about money and responsibility</p>
                </div>
            </header>

            <main className="blog-main">
                {selectedPost ? (
                    <BlogPost
                        post={selectedPost}
                        onBack={() => setSelectedPost(null)}
                        onSignup={onSignup}
                    />
                ) : (
                    <BlogList onSelectPost={setSelectedPost} />
                )}
            </main>

            <footer className="blog-footer">
                <p>© 2026 DadBucks. Teaching kids the value of earning.</p>
            </footer>
        </div>
    );
};

export { blogPosts };
export default Blog;
